import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { formatCurrency } from '../utils/formatters';
import { ProductWithComponents, TariffAnalysisResponse, ReshoringAnalysisResponse, api } from '../services/api';

// Types for view modes
type ViewMode = 'consumer' | 'business' | 'policy' | 'investor';

interface ViewModeButtonProps {
  mode: ViewMode;
  active: boolean;
  icon: string;
  label: string;
  onClick: () => void;
  requiresUpgrade?: boolean;
}

interface UpgradeModalProps {
  isOpen: boolean;
  feature: string;
  currentView: ViewMode;
  productName: string;
  onClose: () => void;
}

interface MetricCardProps {
  title: string;
  value: string | number;
  description: string;
  highlight?: 'red' | 'green' | 'orange' | 'blue';
  'data-testid'?: string;
}

interface FeatureTeaserProps {
  title: string;
  description: string;
  onClick: () => void;
  requiresUpgrade?: boolean;
  'data-testid'?: string;
}

// Access control configuration
const VIEW_MODE_ACCESS = {
  consumer: {
    required_tier: 'free',
    features: ['basic_overview', 'public_data', 'feature_teasers']
  },
  business: {
    required_tier: 'basic',
    features: ['tariff_analysis', 'supply_chain_insights', 'basic_scenarios']
  },
  policy: {
    required_tier: 'premium',
    features: ['policy_modeling', 'economic_impact', 'scenario_comparison']
  },
  investor: {
    required_tier: 'premium', 
    features: ['investment_analysis', 'roi_calculations', 'market_sizing']
  }
};

const canAccessViewMode = (viewMode: ViewMode, userTier: string = 'free', isAdmin: boolean = false): boolean => {
  // Admin users have access to all view modes
  if (isAdmin) {
    return true;
  }
  
  const requiredTier = VIEW_MODE_ACCESS[viewMode]?.required_tier || 'free';
  const tierHierarchy = ['free', 'basic', 'premium', 'enterprise'];
  
  const userTierIndex = tierHierarchy.indexOf(userTier);
  const requiredTierIndex = tierHierarchy.indexOf(requiredTier);
  
  return userTierIndex >= requiredTierIndex;
};

// View Mode Button Component
const ViewModeButton = ({ mode, active, icon, label, onClick, requiresUpgrade = false }: ViewModeButtonProps) => (
  <button
    onClick={onClick}
    data-testid={`view-mode-${mode}`}
    className={`flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium transition-all ${
      active 
        ? 'bg-blue-600 text-white shadow-sm' 
        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
    }`}
  >
    <span className="text-base">{icon}</span>
    <span>{label}</span>
    {requiresUpgrade && (
      <span className="ml-1 px-1.5 py-0.5 bg-yellow-100 text-yellow-800 text-xs rounded">
        Pro
      </span>
    )}
  </button>
);

// Mobile View Mode Switcher
const MobileViewSwitcher = ({ viewMode, onViewChange, userTier, isAdmin }: { 
  viewMode: ViewMode; 
  onViewChange: (mode: ViewMode) => void;
  userTier?: string;
  isAdmin?: boolean;
}) => (
  <div className="lg:hidden bg-white border-b border-gray-200 px-4 py-3">
    <select 
      value={viewMode}
      onChange={(e) => onViewChange(e.target.value as ViewMode)}
      className="w-full border border-gray-300 rounded-lg px-3 py-2"
    >
      <option value="consumer">🛍️ Consumer View</option>
      {(canAccessViewMode('business', userTier, isAdmin)) && (
        <option value="business">🏭 Business View</option>
      )}
      {(!canAccessViewMode('business', userTier, isAdmin)) && (
        <option value="business" disabled>🏭 Business View (Upgrade Required)</option>
      )}
      {(canAccessViewMode('policy', userTier, isAdmin)) && (
        <option value="policy">🏛️ Policy View</option>
      )}
      {(!canAccessViewMode('policy', userTier, isAdmin)) && (
        <option value="policy" disabled>🏛️ Policy View (Upgrade Required)</option>
      )}
      {(canAccessViewMode('investor', userTier, isAdmin)) && (
        <option value="investor">💰 Investor View</option>
      )}
      {(!canAccessViewMode('investor', userTier, isAdmin)) && (
        <option value="investor" disabled>💰 Investor View (Upgrade Required)</option>
      )}
    </select>
  </div>
);

// Metric Card Component
const MetricCard = ({ title, value, description, highlight, ...props }: MetricCardProps) => (
  <div className={`p-4 rounded-lg border ${
    highlight === 'red' ? 'border-red-200 bg-red-50' :
    highlight === 'green' ? 'border-green-200 bg-green-50' :
    highlight === 'orange' ? 'border-orange-200 bg-orange-50' :
    highlight === 'blue' ? 'border-blue-200 bg-blue-50' :
    'border-gray-200 bg-gray-50'
  }`} {...props}>
    <div className="text-center">
      <p className="text-2xl font-bold text-gray-900">{value}</p>
      <p className="text-sm font-medium text-gray-700 mt-1">{title}</p>
      <p className="text-xs text-gray-600 mt-1">{description}</p>
    </div>
  </div>
);

// Feature Teaser Component
const FeatureTeaser = ({ title, description, onClick, requiresUpgrade = true, ...props }: FeatureTeaserProps) => (
  <div className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow" {...props}>
    <div className="flex items-start justify-between">
      <div>
        <h3 className="font-medium text-gray-900 mb-2">{title}</h3>
        <p className="text-sm text-gray-600">{description}</p>
      </div>
      {requiresUpgrade && (
        <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded">
          Premium
        </span>
      )}
    </div>
    <button
      onClick={onClick}
      className="mt-3 w-full px-4 py-2 bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition-colors text-sm font-medium"
    >
      {requiresUpgrade ? 'Upgrade to Access' : 'View Analysis'} →
    </button>
  </div>
);

// Country Breakdown Component
const CountryBreakdown = ({ countries, showSimplified }: { 
  countries: Array<{ country: string; percentage: number }>; 
  showSimplified: boolean; 
}) => (
  <div className="space-y-3">
    {countries?.slice(0, showSimplified ? 3 : 10).map((country, index) => (
      <div key={index} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
        <div className="flex items-center">
          <span className="text-2xl mr-3">
            {country.country === 'China' ? '🇨🇳' :
             country.country === 'Taiwan' ? '🇹🇼' :
             country.country === 'South Korea' ? '🇰🇷' :
             country.country === 'Japan' ? '🇯🇵' :
             country.country === 'United States' ? '🇺🇸' : '🌍'}
          </span>
          <span className="font-medium text-gray-900">{country.country}</span>
        </div>
        <div className="text-right">
          <span className="font-medium text-gray-900">{country.percentage}%</span>
          <p className="text-sm text-gray-600">of production</p>
        </div>
      </div>
    ))}
  </div>
);

// Consumer View Component
const ConsumerView = ({ data, user }: { 
  data: any; 
  user?: any; 
}) => {
  // Check if user has access to features (admin or subscriber)
  const hasBasicAccess = user?.role === 'admin' || (user?.subscription_tier && ['basic', 'premium', 'enterprise'].includes(user.subscription_tier));
  const hasPremiumAccess = user?.role === 'admin' || (user?.subscription_tier && ['premium', 'enterprise'].includes(user.subscription_tier));

  return (
    <div className="space-y-8" data-testid="consumer-view-content">
      {/* Consumer-Focused Analysis with Simple Language */}
      <section className="bg-white rounded-lg shadow p-6" data-testid="consumer-cost-breakdown">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">
          💰 What You're Really Paying For
        </h2>
        
        {/* Loading indicator for consumer data */}
        {data.isLoadingTariff && (
          <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-center space-x-2">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
              <span className="text-sm text-blue-800">Getting the latest price breakdown...</span>
            </div>
          </div>
        )}
        
        <div className="space-y-4">
          <div className="flex items-center justify-between p-4 bg-green-50 rounded-lg">
            <span className="text-gray-700">What it costs to make</span>
            <span className="text-lg font-semibold text-green-600">
              {formatCurrency(data.manufacturing_cost || data.cogs || 447)}
            </span>
          </div>
          <div className="flex items-center justify-between p-4 bg-red-50 rounded-lg">
            <span className="text-gray-700">Extra cost from trade policies</span>
            <span className="text-lg font-semibold text-red-600">
              +{formatCurrency(data.tariff_cost || 89)}
            </span>
          </div>
          <div className="flex items-center justify-between p-4 bg-blue-50 rounded-lg border-2 border-blue-200">
            <span className="font-medium text-gray-900">Your total impact</span>
            <span className="text-xl font-bold text-blue-600">
              {formatCurrency((data.manufacturing_cost || 447) + (data.tariff_cost || 89))}
            </span>
          </div>
        </div>
        
        <div className="mt-4 text-sm text-gray-600">
          {data.tariffData ? (
            <>✅ Real-time data from {data.tariffData.component_breakdown?.length || 0} components</>
          ) : (
            <>📊 Estimated based on typical {data.overview?.category || 'electronics'} products</>
          )}
        </div>
      </section>

      <section className="bg-white rounded-lg shadow p-6" data-testid="consumer-country-breakdown">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">
          🌍 Where It's Really Made
        </h2>
        <CountryBreakdown 
          countries={data.primary_production_countries || [
            { country: 'China', percentage: 45 },
            { country: 'Taiwan', percentage: 33 },
            { country: 'South Korea', percentage: 15 }
          ]}
          showSimplified={true}
        />
      </section>

      <section className="bg-white rounded-lg shadow p-6" data-testid="consumer-feature-teasers">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">
          🔍 Want to Know More?
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FeatureTeaser 
            title="Trade Policy Impact"
            description="See exactly which policies affect this product's price and how much tariffs add to your cost"
            onClick={() => {
              if (hasBasicAccess) {
                // User has access - navigate to dedicated tariff analysis page
                window.location.href = `/products/${data.overview?.slug}/tariff-analysis`;
              } else {
                window.location.href = `/upgrade?product=${encodeURIComponent(data.overview?.slug || 'unknown')}&feature=tariff-analysis`;
              }
            }}
            requiresUpgrade={!hasBasicAccess}
            data-testid="tariff-analysis-teaser"
          />
          <FeatureTeaser 
            title="Alternative Manufacturing"
            description="Explore what it would cost to make this domestically and how it might affect pricing"
            onClick={() => {
              if (hasPremiumAccess) {
                // User has access - navigate to dedicated reshoring analysis page
                window.location.href = `/products/${data.overview?.slug}/reshoring-analysis`;
              } else {
                window.location.href = `/upgrade?product=${encodeURIComponent(data.overview?.slug || 'unknown')}&feature=reshoring-analysis`;
              }
            }}
            requiresUpgrade={!hasPremiumAccess}
          />
        </div>
      </section>
    </div>
  );
};

// Business View Component
const BusinessView = ({ data }: { data: any }) => {
  const cogs = data.cogs || data.manufacturing_cost || 447;
  const tariffCost = data.tariff_cost || 89;
  const totalCogs = cogs + tariffCost;
  const retailPrice = data.retail_price || data.overview?.retail_price || 999;
  const grossMargin = retailPrice - totalCogs;
  const grossMarginPercent = retailPrice > 0 ? ((grossMargin / retailPrice) * 100) : 0;

  return (
    <div className="space-y-8" data-testid="business-view-content">
      {/* Professional Terminology & Business Metrics */}
      <section className="bg-white rounded-lg shadow p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">
          📊 Supply Chain Cost Analysis
        </h2>
        
        {/* Loading indicator for business data */}
        {data.isLoadingTariff && (
          <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-center space-x-2">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
              <span className="text-sm text-blue-800">Loading tariff impact analysis...</span>
            </div>
          </div>
        )}
        
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <MetricCard 
            title="Base COGS"
            value={formatCurrency(cogs)}
            description="Cost of Goods Sold (pre-tariff)"
            data-testid="business-cogs-metric"
          />
          <MetricCard 
            title="Tariff Impact"
            value={`+${formatCurrency(tariffCost)}`}
            description={`${data.tariff_percentage || 19.9}% ${data.tariffData ? 'actual rate' : 'estimated'}`}
            highlight="orange"
          />
          <MetricCard 
            title="Total COGS"
            value={formatCurrency(totalCogs)}
            description="Including tariff impact"
            highlight="red"
          />
          <MetricCard 
            title="Gross Margin"
            value={`${grossMarginPercent.toFixed(1)}%`}
            description={`${formatCurrency(grossMargin)} per unit`}
            highlight={grossMarginPercent > 50 ? "green" : grossMarginPercent > 30 ? "orange" : "red"}
          />
        </div>
        
        {/* Business Impact Analysis */}
        <div className="mt-6 bg-gray-50 rounded-lg p-4">
          <h3 className="font-medium text-gray-900 mb-2">Tariff Business Impact</h3>
          <div className="space-y-2 text-sm text-gray-600">
            <div className="flex justify-between">
              <span>Without Tariffs:</span>
              <span className="font-medium">{formatCurrency(cogs)} COGS, {(((retailPrice - cogs) / retailPrice) * 100).toFixed(1)}% margin</span>
            </div>
            <div className="flex justify-between">
              <span>With Current Tariffs:</span>
              <span className="font-medium text-red-600">{formatCurrency(totalCogs)} COGS, {grossMarginPercent.toFixed(1)}% margin</span>
            </div>
            <div className="flex justify-between">
              <span>Margin Impact:</span>
              <span className="font-medium text-red-600">-{(((retailPrice - cogs) / retailPrice - grossMarginPercent / 100) * 100).toFixed(1)} percentage points</span>
            </div>
          </div>
          {data.tariffData && (
            <div className="mt-3 text-xs text-blue-600">
              ✅ Analysis based on real-time tariff data from {data.tariffData.component_breakdown?.length || 0} components
            </div>
          )}
        </div>
      </section>

      {/* Component-Level Tariff Breakdown */}
      {data.tariffData?.component_breakdown && (
        <section className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">
            🔍 Component Tariff Breakdown
          </h2>
          <div className="space-y-3">
            {data.tariffData.component_breakdown.slice(0, 5).map((component: any, index: number) => (
              <div key={index} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
                <div>
                  <h4 className="font-medium text-gray-900">{component.component_name}</h4>
                  <p className="text-sm text-gray-600">HTS Code: {component.hts_code}</p>
                </div>
                <div className="text-right">
                  <p className="font-medium text-red-600">{formatCurrency(component.tariff_cost_usd)}</p>
                  <p className="text-sm text-gray-600">{component.applicable_rate}% rate</p>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      <section className="bg-white rounded-lg shadow p-6" data-testid="business-strategic-insights">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">
          🎯 Strategic Recommendations
        </h2>
        <div className="space-y-4">
          <div className="border-l-4 border-blue-500 pl-4">
            <h3 className="font-medium text-gray-900">Supply Chain Diversification</h3>
            <p className="text-sm text-gray-600">
              {data.tariffData ? 
                `Geographic risk: ${data.geographic_risk_score}. Consider suppliers in ${data.tariffData.alternative_suppliers?.slice(0, 2).map((s: any) => s.alternatives).flat().slice(0, 3).join(', ') || 'alternative regions'}.` :
                `Reduce geographic concentration risk by developing alternative suppliers in ${data.alternative_suppliers || 3} viable regions`
              }
            </p>
          </div>
          <div className="border-l-4 border-orange-500 pl-4">
            <h3 className="font-medium text-gray-900">Cost Optimization</h3>
            <p className="text-sm text-gray-600">
              Tariffs add {formatCurrency(tariffCost)} to COGS ({((tariffCost / totalCogs) * 100).toFixed(1)}% of total cost) - 
              {data.tariffData ? ' analysis shows highest impact components for prioritized sourcing changes' : ' consider sourcing alternatives or passing costs to customers'}
            </p>
          </div>
          <div className="border-l-4 border-green-500 pl-4">
            <h3 className="font-medium text-gray-900">Reshoring Opportunity</h3>
            <p className="text-sm text-gray-600">
              Domestic manufacturing could eliminate tariff costs while improving supply chain resilience
              {data.reshoringData ? 
                ` (${data.reshoring_roi}% projected ROI, ${data.reshoringData.implementation_timeline?.total_months || 36} month timeline)` :
                ' (18% ROI potential)'
              }
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

// Policy View Component
const PolicyView = ({ data }: { data: any }) => {
  // Calculate policy impact metrics from real data
  const annualTariffRevenue = data.tariffData?.policy_scenarios?.current?.total_cost 
    ? data.tariffData.policy_scenarios.current.total_cost * 1000000 // Scale up for policy view
    : 2400000000;
  
  const jobsSupported = data.reshoringData?.financial_projections?.investment_breakdown?.total 
    ? Math.round(data.reshoringData.financial_projections.investment_breakdown.total / 50000) // Estimate jobs from investment
    : 12000;
    
  const potentialJobs = data.reshoringData?.implementation_timeline?.total_months 
    ? jobsSupported * 3 // Estimate full potential
    : 45000;

  return (
    <div className="space-y-8" data-testid="policy-view-content">
      {/* Policy-Focused Analysis */}
      <section className="bg-white rounded-lg shadow p-6" data-testid="policy-economic-impact">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">
          🏛️ Economic Impact Analysis
        </h2>
        
        {/* Loading indicator */}
        {(data.isLoadingTariff || data.isLoadingReshoring) && (
          <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-center space-x-2">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
              <span className="text-sm text-blue-800">Loading policy impact analysis...</span>
            </div>
          </div>
        )}
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <MetricCard 
            title="Consumer Price Impact"
            value={formatCurrency(data.tariff_cost || 89)}
            description={data.tariffData ? "Actual additional cost per unit" : "Estimated additional cost per unit"}
          />
          <MetricCard 
            title="Tax Revenue Generated"
            value={formatCurrency(annualTariffRevenue)}
            description={data.tariffData ? "Based on current tariff analysis" : "Annual tariff revenue estimate"}
          />
          <MetricCard 
            title="Jobs Impact"
            value={`${jobsSupported} / ${potentialJobs}`}
            description={data.reshoringData ? "Projected domestic manufacturing jobs" : "Current vs. potential US jobs"}
          />
        </div>
        
        {/* Real Policy Scenarios */}
        {data.tariffData?.policy_scenarios && (
          <div className="mt-6 bg-gray-50 rounded-lg p-4">
            <h3 className="font-medium text-gray-900 mb-3">Policy Scenario Analysis</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {Object.entries(data.tariffData.policy_scenarios).map(([scenario, details]: [string, any]) => (
                <div key={scenario} className="bg-white p-3 rounded border">
                  <h4 className="font-medium text-gray-800 capitalize">{scenario.replace('_', ' ')}</h4>
                  <p className="text-sm text-gray-600">Cost: {formatCurrency(details.total_cost)}</p>
                  <p className="text-sm text-gray-600">Probability: {(details.probability * 100).toFixed(0)}%</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </section>

      <section className="bg-white rounded-lg shadow p-6" data-testid="policy-effectiveness-charts">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">
          📈 Policy Effectiveness Analysis
        </h2>
        
        {/* Real Mitigation Strategies */}
        {data.tariffData?.mitigation_strategies && (
          <div className="mb-6">
            <h3 className="font-medium text-gray-900 mb-3">Recommended Policy Adjustments</h3>
            <div className="space-y-3">
              {data.tariffData.mitigation_strategies.slice(0, 3).map((strategy: any, index: number) => (
                <div key={index} className="border border-gray-200 rounded-lg p-3">
                  <h4 className="font-medium text-gray-900">{strategy.strategy}</h4>
                  <p className="text-sm text-gray-600 mt-1">Impact: {strategy.impact}</p>
                  <p className="text-sm text-gray-600">Timeline: {strategy.timeline}</p>
                </div>
              ))}
            </div>
          </div>
        )}
        
        <div data-testid="scenario-comparison-tool">
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="font-medium text-gray-900 mb-2">Scenario Comparison Tool</h3>
            <p className="text-sm text-gray-600">
              {data.tariffData ? 
                `Compare ${Object.keys(data.tariffData.policy_scenarios || {}).length} policy scenarios and their economic impacts` :
                'Compare different tariff scenarios and their economic impacts'
              }
            </p>
          </div>
        </div>
        
        <div className="mt-4" data-testid="jobs-impact-calculator">
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="font-medium text-gray-900 mb-2">Jobs Impact Calculator</h3>
            <p className="text-sm text-gray-600">
              {data.reshoringData ? 
                `Manufacturing reshoring could create ${potentialJobs} jobs with ${formatCurrency(data.reshoringData.financial_projections?.investment_breakdown?.total || 0)} investment` :
                'Calculate employment effects of policy changes'
              }
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

// Investor View Component
const InvestorView = ({ data }: { data: any }) => {
  // Calculate investment metrics from real data
  const marketSize = data.reshoringData?.financial_projections?.current_cost 
    ? data.reshoringData.financial_projections.current_cost * 1000 // Scale up for market view
    : 50000000000;
    
  const reshoringROI = data.reshoringData?.financial_projections?.roi_metrics?.roi_5_year || data.reshoring_roi || 18;
  const paybackMonths = data.reshoringData?.financial_projections?.roi_metrics?.payback_period_months || 36;
  const riskScore = data.reshoringData?.risk_assessment?.overall_score || 'Medium';

  return (
    <div className="space-y-8" data-testid="investor-view-content">
      {/* Investment-Focused Analysis */}
      <section className="bg-white rounded-lg shadow p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">
          💰 Investment Opportunity Analysis
        </h2>
        
        {/* Loading indicator */}
        {data.isLoadingReshoring && (
          <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-center space-x-2">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
              <span className="text-sm text-blue-800">Loading investment analysis...</span>
            </div>
          </div>
        )}
        
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <MetricCard 
            title="Market Size"
            value={formatCurrency(marketSize)}
            description={data.reshoringData ? "Addressable manufacturing market" : "Total addressable market"}
            data-testid="investor-market-size"
          />
          <MetricCard 
            title="Reshoring ROI"
            value={`${reshoringROI.toFixed(1)}%`}
            description={data.reshoringData ? "5-year projected return" : "Expected return on investment"}
            data-testid="investor-roi-metrics"
          />
          <MetricCard 
            title="Payback Period"
            value={`${paybackMonths} months`}
            description={data.reshoringData ? "Based on financial projections" : "Capital recovery timeline"}
          />
          <MetricCard 
            title="Risk Score"
            value={riskScore}
            description={data.reshoringData ? "Comprehensive risk assessment" : "Investment risk assessment"}
            data-testid="investor-risk-assessment"
          />
        </div>
        
        {/* Real Investment Breakdown */}
        {data.reshoringData?.financial_projections?.investment_breakdown && (
          <div className="mt-6 bg-gray-50 rounded-lg p-4">
            <h3 className="font-medium text-gray-900 mb-3">Investment Breakdown</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {Object.entries(data.reshoringData.financial_projections.investment_breakdown)
                .filter(([key]) => key !== 'total')
                .map(([category, amount]: [string, any]) => (
                <div key={category} className="text-center">
                  <p className="text-lg font-semibold text-gray-900">{formatCurrency(amount)}</p>
                  <p className="text-sm text-gray-600 capitalize">{category.replace('_', ' ')}</p>
                </div>
              ))}
            </div>
            <div className="mt-3 pt-3 border-t text-center">
              <p className="text-xl font-bold text-gray-900">
                Total: {formatCurrency(data.reshoringData.financial_projections.investment_breakdown.total)}
              </p>
            </div>
          </div>
        )}
      </section>

      <section className="bg-white rounded-lg shadow p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">
          📊 Due Diligence Summary
        </h2>
        
        {/* Real Risk Assessment */}
        {data.reshoringData?.risk_assessment && (
          <div className="mb-6">
            <h3 className="font-medium text-gray-900 mb-3">Risk Analysis</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {Object.entries(data.reshoringData.risk_assessment.risk_categories).map(([category, risk]: [string, any]) => (
                <div key={category} className="border border-gray-200 rounded-lg p-3">
                  <h4 className="font-medium text-gray-900 capitalize">{category}</h4>
                  <p className="text-sm text-gray-600">{risk}</p>
                </div>
              ))}
            </div>
          </div>
        )}
        
        {/* Real Scenario Analysis */}
        {data.reshoringData?.scenario_analysis && (
          <div className="mb-6">
            <h3 className="font-medium text-gray-900 mb-3">Scenario Analysis</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {Object.entries(data.reshoringData.scenario_analysis).map(([scenario, details]: [string, any]) => (
                <div key={scenario} className="border border-gray-200 rounded-lg p-3">
                  <h4 className="font-medium text-gray-900 capitalize">{scenario.replace('_', ' ')}</h4>
                  <p className="text-sm text-gray-600">ROI: {details.roi}%</p>
                  <p className="text-sm text-gray-600">Timeline: {details.timeline_months} months</p>
                  <p className="text-sm text-gray-600">Probability: {(details.probability * 100).toFixed(0)}%</p>
                </div>
              ))}
            </div>
          </div>
        )}
        
        <div data-testid="due-diligence-report">
          <div className="space-y-4">
            <div className="border border-gray-200 rounded-lg p-4">
              <h3 className="font-medium text-gray-900 mb-2">Market Opportunity</h3>
              <p className="text-sm text-gray-600">
                {data.reshoringData ? 
                  `${data.reshoringData.feasibility_assessment?.overall_feasibility || 'HIGH'} feasibility for domestic manufacturing with ${data.reshoringData.feasibility_assessment?.feasibility_score || 75}% confidence score` :
                  'Strong demand for domestic manufacturing capabilities in electronics sector'
                }
              </p>
            </div>
            <div className="border border-gray-200 rounded-lg p-4">
              <h3 className="font-medium text-gray-900 mb-2">Financial Projections</h3>
              <p className="text-sm text-gray-600">
                {data.reshoringData ? 
                  `${reshoringROI.toFixed(1)}% IRR over ${Math.round(paybackMonths / 12)}-year investment horizon. NPV: ${formatCurrency(data.reshoringData.financial_projections?.roi_metrics?.npv_5_year || 0)}` :
                  '18% IRR over 5-year investment horizon with moderate risk profile'
                }
              </p>
            </div>
            
            {/* Implementation Timeline */}
            {data.reshoringData?.implementation_timeline && (
              <div className="border border-gray-200 rounded-lg p-4">
                <h3 className="font-medium text-gray-900 mb-2">Implementation Plan</h3>
                <p className="text-sm text-gray-600 mb-2">
                  Total timeline: {data.reshoringData.implementation_timeline.total_months} months
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {data.reshoringData.implementation_timeline.phases?.slice(0, 4).map((phase: any, index: number) => (
                    <div key={index} className="text-xs text-gray-500">
                      {phase.phase}: {phase.months} months
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

// Smart Upgrade Modal Component
const SmartUpgradeModal = ({ isOpen, feature, currentView, productName, onClose }: UpgradeModalProps) => {
  if (!isOpen) return null;

  const getFeatureDisplayName = (feature: string) => {
    const displayNames: Record<string, string> = {
      tariff_analysis: 'Tariff Analysis',
      reshoring_analysis: 'Reshoring Analysis',
      policy_modeling: 'Policy Modeling',
      investment_analysis: 'Investment Analysis'
    };
    return displayNames[feature] || feature;
  };

  const getFeatureValueProposition = (feature: string, view: ViewMode) => {
    const propositions: Record<string, Record<ViewMode, string>> = {
      tariff_analysis: {
        consumer: 'See exactly how much tariffs add to your purchase price and explore alternatives.',
        business: 'Analyze tariff exposure across your supply chain and model risk scenarios.',
        policy: 'Model economic impacts of different tariff policies on industry and consumers.',
        investor: 'Evaluate investment opportunities in tariff-affected industries.'
      }
    };
    return propositions[feature]?.[view] || 'Unlock advanced analysis features for this product.';
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" data-testid="upgrade-modal">
      <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">
            Unlock {getFeatureDisplayName(feature)}
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600" data-testid="upgrade-modal-close">✕</button>
        </div>
        
        <div className="space-y-4">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm text-blue-800">
              <strong>For {productName} in {currentView} view:</strong><br />
              {getFeatureValueProposition(feature, currentView)}
            </p>
          </div>
          
          <div className="space-y-3">
            <button className="w-full px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium">
              Start Basic Plan - $19/mo
            </button>
            <button className="w-full px-4 py-3 border border-blue-600 text-blue-600 rounded-lg hover:bg-blue-50 font-medium">
              7-Day Free Trial
            </button>
          </div>
          
          <div className="text-center">
            <p className="text-xs text-gray-600">
              7-day free trial • Cancel anytime
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

// Main Product Analysis Component with Views
const ProductAnalysisWithViews = ({ product }: { product: ProductWithComponents }) => {
  const [viewMode, setViewMode] = useState<ViewMode>('consumer');
  const [upgradeModal, setUpgradeModal] = useState<{
    isOpen: boolean;
    feature: string;
    currentView: ViewMode;
    productName: string;
  }>({
    isOpen: false,
    feature: '',
    currentView: 'consumer',
    productName: ''
  });
  
  // Tariff and reshoring data state
  const [tariffData, setTariffData] = useState<TariffAnalysisResponse | null>(null);
  const [reshoringData, setReshoringData] = useState<ReshoringAnalysisResponse | null>(null);
  const [isLoadingTariff, setIsLoadingTariff] = useState(false);
  const [isLoadingReshoring, setIsLoadingReshoring] = useState(false);
  const [tariffError, setTariffError] = useState<string | null>(null);
  
  const { user } = useAuth();

  // Load tariff and reshoring data
  useEffect(() => {
    const loadTariffData = async () => {
      if (!product.slug) return;
      
      setIsLoadingTariff(true);
      setTariffError(null);
      
      try {
        const response = await api.getProductTariffAnalysis(product.slug);
        setTariffData(response.data);
      } catch (error) {
        console.error('Failed to load tariff data:', error);
        setTariffError('Failed to load tariff analysis');
        // Keep mock data as fallback
      } finally {
        setIsLoadingTariff(false);
      }
    };

    const loadReshoringData = async () => {
      if (!product.slug) return;
      
      setIsLoadingReshoring(true);
      
      try {
        const response = await api.getProductReshoringAnalysis(product.slug);
        setReshoringData(response.data);
      } catch (error) {
        console.error('Failed to load reshoring data:', error);
        // Keep mock data as fallback
      } finally {
        setIsLoadingReshoring(false);
      }
    };

    // Only load data if user has access (admin or subscriber)
    if (user?.role === 'admin' || (user?.subscription_tier && ['basic', 'premium', 'enterprise'].includes(user.subscription_tier))) {
      loadTariffData();
      loadReshoringData();
    }
  }, [product.slug, user?.role, user?.subscription_tier]);

  // View mode change handler with upgrade prompts
  const handleViewModeChange = (newMode: ViewMode) => {
    if (!canAccessViewMode(newMode, user?.subscription_tier, user?.role === 'admin')) {
      // Show upgrade modal only when user tries to access restricted view
      setUpgradeModal({
        isOpen: true,
        feature: `${newMode}_view`,
        currentView: viewMode,
        productName: product.name
      });
    } else {
      setViewMode(newMode);
    }
  };

  // Prepare data for views with real tariff data
  const baseData = {
    overview: product,
    components: product.components,
    manufacturing_cost: product.manufacturing_cost,
    retail_price: product.retail_price,
    // Use real tariff data if available, otherwise fallback to reasonable estimate
    tariff_cost: tariffData?.total_tariff_cost_usd || (product.manufacturing_cost ? product.manufacturing_cost * 0.2 : 89),
    tariff_percentage: tariffData?.tariff_percentage || 19.9,
    primary_production_countries: product.primary_production_countries,
    cogs: product.manufacturing_cost,
    geographic_risk_score: tariffData?.geographic_risk?.overall_risk || 'High',
    alternative_suppliers: tariffData?.alternative_suppliers?.length || 3,
    // Reshoring data
    reshoring_feasibility_score: reshoringData?.feasibility_assessment?.feasibility_score || 75,
    reshoring_roi: reshoringData?.financial_projections?.roi_metrics?.roi_5_year || 18,
    // Loading states
    isLoadingTariff,
    isLoadingReshoring,
    tariffError,
    // Raw analysis data for advanced views
    tariffData,
    reshoringData
  };

  const renderViewContent = () => {
    switch (viewMode) {
      case 'consumer':
        return <ConsumerView data={baseData} user={user} />;
      case 'business':
        return <BusinessView data={baseData} />;
      case 'policy':
        return <PolicyView data={baseData} />;
      case 'investor':
        return <InvestorView data={baseData} />;
      default:
        return <ConsumerView data={baseData} user={user} />;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* View Mode Switcher - Always Visible */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <h1 className="text-xl font-bold text-gray-900">{product.name} Analysis</h1>
            
            {/* Desktop View Mode Toggle */}
            <div className="hidden lg:flex items-center space-x-1 bg-gray-100 rounded-lg p-1" data-testid="view-mode-switcher">
              <ViewModeButton 
                mode="consumer" 
                active={viewMode === 'consumer'}
                icon="🛍️" 
                label="Consumer"
                onClick={() => handleViewModeChange('consumer')}
              />
              <ViewModeButton 
                mode="business" 
                active={viewMode === 'business'}
                icon="🏭" 
                label="Business"
                onClick={() => handleViewModeChange('business')}
                requiresUpgrade={!canAccessViewMode('business', user?.subscription_tier, user?.role === 'admin')}
              />
              <ViewModeButton 
                mode="policy" 
                active={viewMode === 'policy'}
                icon="🏛️" 
                label="Policy"
                onClick={() => handleViewModeChange('policy')}
                requiresUpgrade={!canAccessViewMode('policy', user?.subscription_tier, user?.role === 'admin')}
              />
              <ViewModeButton 
                mode="investor" 
                active={viewMode === 'investor'}
                icon="💰" 
                label="Investor"
                onClick={() => handleViewModeChange('investor')}
                requiresUpgrade={!canAccessViewMode('investor', user?.subscription_tier, user?.role === 'admin')}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Mobile View Mode Switcher */}
      <MobileViewSwitcher viewMode={viewMode} onViewChange={handleViewModeChange} userTier={user?.subscription_tier} isAdmin={user?.role === 'admin'} />

      {/* Dynamic Content Based on View Mode */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {renderViewContent()}
      </div>

      {/* Upgrade Modal */}
      <SmartUpgradeModal 
        isOpen={upgradeModal.isOpen}
        feature={upgradeModal.feature}
        currentView={upgradeModal.currentView}
        productName={upgradeModal.productName}
        onClose={() => setUpgradeModal({ ...upgradeModal, isOpen: false })}
      />
    </div>
  );
};

export default ProductAnalysisWithViews; 